import React, { useState, useMemo, useRef } from "react";
import useAutoSave from "../hooks/useAutoSave";
import AutoSaveStatus from "./AutoSaveStatus";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Divider,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ShortTextIcon from "@mui/icons-material/ShortText";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  line: "var(--color-surface-press-light)",
  line2: "var(--color-surface-press-light)",
  surface2: "var(--color-surface-2)",
  surface3: "var(--color-surface-2)",
  blue: "var(--color-primary)",
  danger: "#B42318",
  draftBg: "#FEF6E7",
  draftFg: "#9A6700",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
let _qid = 0;
const nextQid = () => `q${++_qid}`;
const nextOid = () => `o${++_qid}`;

function makeQuestion(type) {
  if (type === "TRUE_FALSE") {
    return {
      id: nextQid(),
      type: "TRUE_FALSE",
      questionText: "",
      points: 10,
      tfCorrect: true,
      options: [
        { id: nextOid(), text: "True", isCorrect: true },
        { id: nextOid(), text: "False", isCorrect: false },
      ],
    };
  }
  if (type === "MULTIPLE_SELECT") {
    return {
      id: nextQid(),
      type: "MULTIPLE_SELECT",
      questionText: "",
      points: 10,
      options: [
        { id: nextOid(), text: "", isCorrect: false },
        { id: nextOid(), text: "", isCorrect: false },
        { id: nextOid(), text: "", isCorrect: false },
        { id: nextOid(), text: "", isCorrect: false },
      ],
    };
  }
  if (type === "SHORT_ANSWER") {
    return {
      id: nextQid(),
      type: "SHORT_ANSWER",
      questionText: "",
      points: 10,
      acceptedAnswers: [""],
    };
  }
  if (type === "MATCHING") {
    return {
      id: nextQid(),
      type: "MATCHING",
      questionText: "",
      points: 10,
      pairs: [
        { id: nextOid(), prompt: "", answer: "" },
        { id: nextOid(), prompt: "", answer: "" },
        { id: nextOid(), prompt: "", answer: "" },
      ],
    };
  }
  return {
    id: nextQid(),
    type: "MULTIPLE_CHOICE",
    questionText: "",
    points: 10,
    options: [
      { id: nextOid(), text: "", isCorrect: false },
      { id: nextOid(), text: "", isCorrect: false },
      { id: nextOid(), text: "", isCorrect: false },
      { id: nextOid(), text: "", isCorrect: false },
    ],
  };
}

// Returns an error message per question id for content the server would reject
// (blank prompt, <2 filled options, no correct answer, no accepted answer, <2 full pairs).
export function validateQuestions(questions) {
  const errs = {};
  questions.forEach((q) => {
    const filled = (q.options || []).filter((o) => (o.text || "").trim());
    let m = null;
    if (!(q.questionText || "").trim()) m = "Enter the question prompt.";
    else if (q.type === "MULTIPLE_CHOICE" || q.type === "MULTIPLE_SELECT") {
      if (filled.length < 2) m = "Enter at least two answer options.";
      else if (!filled.some((o) => o.isCorrect)) m = "Mark a correct answer (with text).";
    } else if (q.type === "SHORT_ANSWER") {
      if (!(q.acceptedAnswers || []).some((a) => (a || "").trim())) m = "Add at least one accepted answer.";
    } else if (q.type === "MATCHING") {
      const half = (q.pairs || []).some((p) => !!(p.prompt || "").trim() !== !!(p.answer || "").trim());
      const full = (q.pairs || []).filter((p) => (p.prompt || "").trim() && (p.answer || "").trim());
      if (half) m = "Fill in both sides of every pair, or clear the pair.";
      else if (full.length < 2) m = "Add at least two matching pairs.";
    }
    if (m) errs[q.id] = m;
  });
  return errs;
}

// Drops blank options/pairs so only the filled ones are sent.
function cleanQuestion(q) {
  if (q.type === "SHORT_ANSWER") return { ...q, acceptedAnswers: q.acceptedAnswers.map((a) => a.trim()).filter(Boolean) };
  if (q.type === "MULTIPLE_CHOICE" || q.type === "MULTIPLE_SELECT") return { ...q, options: q.options.filter((o) => (o.text || "").trim()) };
  if (q.type === "MATCHING") return { ...q, pairs: q.pairs.filter((p) => (p.prompt || "").trim() && (p.answer || "").trim()) };
  return q;
}

// ISO datetime string → "YYYY-MM-DDTHH:MM" for datetime-local input
function toInputDt(iso) {
  if (!iso) return "";
  return iso.slice(0, 16);
}

function emptyForm() {
  return { title: "", description: "", category: "", passingScore: "", timeLimitMinutes: "", maxRetakes: "1", opensAt: "", closesAt: "", questions: [], changeNote: "" };
}

function hydrateInitial(initial) {
  if (!initial) return emptyForm();
  const qs = (initial.questions || []).map((q) => {
    const base = { ...q, id: q.id ?? nextQid(), points: q.points ?? 10 };
    if (q.type === "MATCHING") {
      return {
        ...base,
        pairs: (q.pairs || []).map((p) => ({ ...p, id: p.id ?? nextOid() })),
      };
    }
    if (q.type === "SHORT_ANSWER") {
      const accepted = Array.isArray(q.acceptedAnswers) ? q.acceptedAnswers.map(String) : [];
      return { ...base, acceptedAnswers: accepted.length > 0 ? accepted : [""] };
    }
    return {
      ...base,
      tfCorrect:
        q.type === "TRUE_FALSE"
          ? (q.options?.find((o) => o.isCorrect)?.text === "True" ?? true)
          : undefined,
      options: (q.options || []).map((o) => ({ ...o, id: o.id ?? nextOid() })),
    };
  });
  const existingTags = (() => { try { const v = JSON.parse(initial.tags || "[]"); return Array.isArray(v) ? v : []; } catch { return []; } })();
  const existingCategory = existingTags.find(t => ["Firefighter","EMS","Hazmat","Search & Rescue","Fire Officer","General"].includes(t)) || "";
  return {
    title: initial.title || "",
    description: initial.description || "",
    category: initial.category || existingCategory,
    passingScore: initial.passingScore != null ? String(initial.passingScore) : "",
    timeLimitMinutes: initial.timeLimitMinutes != null ? String(initial.timeLimitMinutes) : "",
    maxRetakes: initial.maxRetakes != null ? String(initial.maxRetakes) : "1",
    opensAt: toInputDt(initial.opensAt),
    closesAt: toInputDt(initial.closesAt),
    questions: qs,
    changeNote: "",
  };
}

// ─── SummaryStat tile ─────────────────────────────────────────────────────────
function SummaryStat({ label, value, icon, color }) {
  return (
    <Box sx={{
      background: C.surface2,
      borderRadius: "10px",
      border: `1px solid ${C.line2}`,
      p: "10px 12px",
      minWidth: 0,
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
        <Box sx={{ color, display: "flex", "& svg": { fontSize: 14 } }}>{icon}</Box>
        <Typography sx={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
          textTransform: "uppercase", color: C.ink3,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{label}</Typography>
      </Box>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: C.ink, fontVariantNumeric: "tabular-nums", fontFamily: "var(--font-display)" }}>
        {value}
      </Typography>
    </Box>
  );
}

// ─── QuestionEditor ───────────────────────────────────────────────────────────
function QuestionEditor({ q, n, onChange, onDelete, error }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tSecondary = theme.palette.secondary.main;
  const tAccent = theme.palette.warning.main;
  const TYPE_CONFIG = {
    MULTIPLE_CHOICE: {
      bg: "rgba(37,99,235,.10)", fg: C.blue,
      label: "Multiple choice", icon: <FormatListBulletedIcon sx={{ fontSize: 13 }} />,
    },
    TRUE_FALSE: {
      bg: alpha(tSecondary, 0.10), fg: tSecondary,
      label: "True / false", icon: <ToggleOffIcon sx={{ fontSize: 13 }} />,
    },
    MULTIPLE_SELECT: {
      bg: alpha(tAccent, 0.10), fg: tAccent,
      label: "Select all that apply", icon: <CheckBoxOutlinedIcon sx={{ fontSize: 13 }} />,
    },
    MATCHING: {
      bg: "rgba(124,58,237,.10)", fg: "var(--color-accent-violet)",
      label: "Matching", icon: <SwapHorizIcon sx={{ fontSize: 13 }} />,
    },
    SHORT_ANSWER: {
      bg: "rgba(13,148,136,.10)", fg: "#0D9488",
      label: "Short answer", icon: <ShortTextIcon sx={{ fontSize: 13 }} />,
    },
  };
  const accent = TYPE_CONFIG[q.type] || TYPE_CONFIG.MULTIPLE_CHOICE;

  const setCorrect = (oIdx) => {
    onChange({ ...q, options: q.options.map((o, i) => ({ ...o, isCorrect: i === oIdx })) });
  };

  const setTF = (val) => {
    onChange({
      ...q,
      tfCorrect: val,
      options: q.options.map((o) => ({ ...o, isCorrect: o.text === (val ? "True" : "False") })),
    });
  };

  const toggleMultiCorrect = (optId) => {
    onChange({
      ...q,
      options: q.options.map((o) => o.id === optId ? { ...o, isCorrect: !o.isCorrect } : o),
    });
  };

  return (
    <Box sx={{
      background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px",
      p: "14px", display: "flex", flexDirection: "column", gap: "10px",
    }}>
      {/* header row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box sx={{
          width: 24, height: 24, borderRadius: "6px", background: C.surface3,
          color: C.ink2, fontSize: 11, fontWeight: 700, display: "flex",
          alignItems: "center", justifyContent: "center", fontVariantNumeric: "tabular-nums",
          flexShrink: 0,
        }}>{n}</Box>
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: "5px",
          height: 22, px: "8px", borderRadius: "999px",
          background: accent.bg, color: accent.fg,
          fontSize: 11, fontWeight: 600,
        }}>
          {accent.icon} {accent.label}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <TextField
            type="number"
            size="small"
            value={q.points}
            onChange={(e) => onChange({ ...q, points: Math.max(0, parseInt(e.target.value || "0", 10)) })}
            inputProps={{ min: 0, style: { width: 40, padding: "4px 6px", fontSize: 12, textAlign: "right" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "7px",
                background: "#fff",
                "& fieldset": { borderColor: C.line },
                "&:hover fieldset": { borderColor: C.ink3 },
                "&.Mui-focused fieldset": { borderColor: tPrimary },
              },
            }}
          />
          <Typography sx={{ fontSize: 11, color: C.ink3 }}>pts</Typography>
          <Tooltip title="Remove question">
            <IconButton size="small" onClick={onDelete}
              sx={{ width: 28, height: 28, color: C.ink3, "&:hover": { background: C.surface2 } }}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* prompt input */}
      <TextField
        fullWidth
        size="small"
        placeholder="Question prompt…"
        error={Boolean(error)}
        value={q.questionText}
        onChange={(e) => onChange({ ...q, questionText: e.target.value })}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            background: "#fff",
            fontSize: 13,
            "& fieldset": { borderColor: C.line },
            "&:hover fieldset": { borderColor: C.ink3 },
            "&.Mui-focused fieldset": { borderColor: tPrimary },
          },
        }}
      />

      {error && (
        <Typography role="alert" data-testid={`question-error-${q.id}`} sx={{ fontSize: 12, color: C.danger }}>{error}</Typography>
      )}

      {/* MULTIPLE_CHOICE options */}
      {q.type === "MULTIPLE_CHOICE" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {q.options.map((opt, i) => (
            <Box key={opt.id} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box
                component="button"
                onClick={() => setCorrect(i)}
                title={opt.isCorrect ? "Correct answer" : "Mark correct"}
                sx={{
                  width: 18, height: 18, borderRadius: "50%", cursor: "pointer",
                  border: `1.5px solid ${opt.isCorrect ? C.blue : "var(--color-hairline-cool)"}`,
                  background: opt.isCorrect ? C.blue : "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  p: 0, flexShrink: 0, outline: "none",
                  "&:focus-visible": { boxShadow: `0 0 0 3px rgba(37,99,235,.25)` },
                }}
              >
                {opt.isCorrect && (
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />
                )}
              </Box>
              <TextField
                size="small"
                fullWidth
                placeholder={`Option ${i + 1}`}
                value={opt.text}
                onChange={(e) => onChange({
                  ...q,
                  options: q.options.map((o) => o.id === opt.id ? { ...o, text: e.target.value } : o),
                })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "7px", height: 32, fontSize: 13, background: "#fff",
                    "& fieldset": { borderColor: C.line },
                    "&:hover fieldset": { borderColor: C.ink3 },
                    "&.Mui-focused fieldset": { borderColor: tPrimary },
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* TRUE_FALSE buttons */}
      {q.type === "TRUE_FALSE" && (
        <Box sx={{ display: "flex", gap: "8px" }}>
          {[{ val: true, label: "True" }, { val: false, label: "False" }].map(({ val, label }) => {
            const sel = q.tfCorrect === val;
            return (
              <Box
                key={label}
                component="button"
                onClick={() => setTF(val)}
                sx={{
                  flex: 1, height: 34, borderRadius: "8px", cursor: "pointer",
                  border: `1.5px solid ${sel ? tSecondary : C.line}`,
                  background: sel ? alpha(tSecondary, 0.06) : "#fff",
                  color: sel ? tSecondary : C.ink2,
                  fontWeight: 600, fontSize: 13, fontFamily: "inherit",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                  outline: "none",
                  "&:focus-visible": { boxShadow: `0 0 0 3px ${alpha(tSecondary, 0.25)}` },
                }}
              >
                {sel && <CheckIcon sx={{ fontSize: 14 }} />}
                {label}
              </Box>
            );
          })}
        </Box>
      )}

      {/* MULTIPLE_SELECT options — square checkbox toggle, multiple can be correct */}
      {q.type === "MULTIPLE_SELECT" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <Typography sx={{ fontSize: 11, color: C.ink3, mb: "2px" }}>
            Click square to mark as correct. Multiple answers can be correct.
          </Typography>
          {q.options.map((opt, i) => (
            <Box key={opt.id} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box
                component="button"
                onClick={() => toggleMultiCorrect(opt.id)}
                title={opt.isCorrect ? "Correct — click to unmark" : "Mark as correct"}
                sx={{
                  width: 18, height: 18, borderRadius: "4px", cursor: "pointer",
                  border: `1.5px solid ${opt.isCorrect ? tAccent : "var(--color-hairline-cool)"}`,
                  background: opt.isCorrect ? tAccent : "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  p: 0, flexShrink: 0, outline: "none",
                  "&:focus-visible": { boxShadow: `0 0 0 3px ${alpha(tAccent, 0.25)}` },
                }}
              >
                {opt.isCorrect && <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />}
              </Box>
              <TextField
                size="small"
                fullWidth
                placeholder={`Option ${i + 1}`}
                value={opt.text}
                onChange={(e) => onChange({
                  ...q,
                  options: q.options.map((o) => o.id === opt.id ? { ...o, text: e.target.value } : o),
                })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "7px", height: 32, fontSize: 13, background: "#fff",
                    "& fieldset": { borderColor: C.line },
                    "&:hover fieldset": { borderColor: C.ink3 },
                    "&.Mui-focused fieldset": { borderColor: tPrimary },
                  },
                }}
              />
              <Tooltip title="Remove option">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onChange({ ...q, options: q.options.filter((o) => o.id !== opt.id) })}
                    disabled={q.options.length <= 2}
                    sx={{ width: 26, height: 26, color: C.ink3, flexShrink: 0 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
          <Box
            component="button"
            onClick={() => onChange({ ...q, options: [...q.options, { id: nextOid(), text: "", isCorrect: false }] })}
            sx={{
              alignSelf: "flex-start", height: 28, px: "10px", borderRadius: "6px",
              border: `1px dashed ${C.line}`, background: "transparent",
              color: C.ink3, fontSize: 12, fontWeight: 600, cursor: "pointer",
              fontFamily: "inherit",
              "&:hover": { background: C.surface2, color: C.ink2 },
            }}
          >
            + Add option
          </Box>
        </Box>
      )}

      {/* MATCHING pairs — prompt / answer columns */}
      {q.type === "MATCHING" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 28px", gap: "8px", mb: "2px" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.ink3, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Prompt
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.ink3, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Answer
            </Typography>
            <Box />
          </Box>
          {q.pairs.map((pair, i) => (
            <Box key={pair.id} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 28px", gap: "8px", alignItems: "center" }}>
              <TextField
                size="small" placeholder={`Prompt ${i + 1}`} value={pair.prompt}
                onChange={(e) => onChange({ ...q, pairs: q.pairs.map((p) => p.id === pair.id ? { ...p, prompt: e.target.value } : p) })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "7px", height: 32, fontSize: 13,
                    "& fieldset": { borderColor: C.line },
                    "&:hover fieldset": { borderColor: C.ink3 },
                    "&.Mui-focused fieldset": { borderColor: tPrimary },
                  },
                }}
              />
              <TextField
                size="small" placeholder={`Answer ${i + 1}`} value={pair.answer}
                onChange={(e) => onChange({ ...q, pairs: q.pairs.map((p) => p.id === pair.id ? { ...p, answer: e.target.value } : p) })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "7px", height: 32, fontSize: 13,
                    "& fieldset": { borderColor: C.line },
                    "&:hover fieldset": { borderColor: C.ink3 },
                    "&.Mui-focused fieldset": { borderColor: tPrimary },
                  },
                }}
              />
              <Tooltip title="Remove pair">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onChange({ ...q, pairs: q.pairs.filter((p) => p.id !== pair.id) })}
                    disabled={q.pairs.length <= 2}
                    sx={{ width: 28, height: 28, color: C.ink3 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
          <Box
            component="button"
            onClick={() => onChange({ ...q, pairs: [...q.pairs, { id: nextOid(), prompt: "", answer: "" }] })}
            sx={{
              alignSelf: "flex-start", height: 28, px: "10px", borderRadius: "6px",
              border: `1px dashed ${C.line}`, background: "transparent",
              color: C.ink3, fontSize: 12, fontWeight: 600, cursor: "pointer",
              fontFamily: "inherit",
              "&:hover": { background: C.surface2, color: C.ink2 },
            }}
          >
            + Add pair
          </Box>
        </Box>
      )}

      {/* SHORT_ANSWER accepted answers — auto-graded, case/whitespace-insensitive */}
      {q.type === "SHORT_ANSWER" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.ink3, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Accepted answers
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.ink3 }}>
            A student's answer is marked correct if it matches any of these (ignoring capitalization and extra spaces).
          </Typography>
          {q.acceptedAnswers.map((ans, i) => (
            <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1fr 28px", gap: "8px", alignItems: "center" }}>
              <TextField
                size="small" placeholder={`Accepted answer ${i + 1}`} value={ans}
                inputProps={{ "aria-label": `Accepted answer ${i + 1}` }}
                onChange={(e) => onChange({ ...q, acceptedAnswers: q.acceptedAnswers.map((a, k) => (k === i ? e.target.value : a)) })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "7px", height: 32, fontSize: 13,
                    "& fieldset": { borderColor: C.line },
                    "&:hover fieldset": { borderColor: C.ink3 },
                    "&.Mui-focused fieldset": { borderColor: tPrimary },
                  },
                }}
              />
              <Tooltip title="Remove answer">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Remove accepted answer ${i + 1}`}
                    onClick={() => onChange({ ...q, acceptedAnswers: q.acceptedAnswers.filter((_, k) => k !== i) })}
                    disabled={q.acceptedAnswers.length <= 1}
                    sx={{ width: 28, height: 28, color: C.ink3 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
          <Box
            component="button"
            onClick={() => onChange({ ...q, acceptedAnswers: [...q.acceptedAnswers, ""] })}
            sx={{
              alignSelf: "flex-start", height: 28, px: "10px", borderRadius: "6px",
              border: `1px dashed ${C.line}`, background: "transparent",
              color: C.ink3, fontSize: 12, fontWeight: 600, cursor: "pointer",
              fontFamily: "inherit",
              "&:hover": { background: C.surface2, color: C.ink2 },
            }}
          >
            + Add accepted answer
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── PreviewQuestion ──────────────────────────────────────────────────────────
function PreviewQuestion({ q, n }) {
  const isMC = q.type === "MULTIPLE_CHOICE";
  const isTF = q.type === "TRUE_FALSE";
  const isMS = q.type === "MULTIPLE_SELECT";
  const isMatch = q.type === "MATCHING";

  return (
    <Box sx={{ border: `1px solid ${C.line}`, borderRadius: "10px", p: "14px", background: "#fff", mb: "10px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: "6px" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.ink3 }}>Question {n}</Typography>
        <Typography sx={{ fontSize: 11, color: C.ink3 }}>{q.points} pts</Typography>
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: q.questionText ? C.ink : "var(--color-ink-4)" }}>
        {q.questionText || "Untitled question"}
      </Typography>

      {isMC && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: "12px" }}>
          {q.options.map((o, i) => (
            <Box key={o.id} sx={{
              display: "flex", alignItems: "center", gap: "10px",
              p: "8px 12px", border: `1px solid ${C.line}`, borderRadius: "8px",
              fontSize: 13, color: o.text ? C.ink : "var(--color-ink-4)",
            }}>
              <Box sx={{ width: 15, height: 15, borderRadius: "50%", border: `1.5px solid var(--color-hairline-cool)`, flexShrink: 0 }} />
              {o.text || `Option ${i + 1}`}
            </Box>
          ))}
        </Box>
      )}

      {isTF && (
        <Box sx={{ display: "flex", gap: "8px", mt: "12px" }}>
          {["True", "False"].map((t) => (
            <Box key={t} sx={{
              flex: 1, p: "9px 12px", borderRadius: "8px",
              border: `1px solid ${C.line}`, background: "#fff",
              fontSize: 13, color: C.ink, display: "flex", alignItems: "center", gap: "10px",
            }}>
              <Box sx={{ width: 15, height: 15, borderRadius: "50%", border: `1.5px solid var(--color-hairline-cool)`, flexShrink: 0 }} />
              {t}
            </Box>
          ))}
        </Box>
      )}

      {isMS && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: "12px" }}>
          <Typography sx={{ fontSize: 11, color: C.ink3, fontStyle: "italic" }}>Select all that apply</Typography>
          {q.options.map((o, i) => (
            <Box key={o.id} sx={{
              display: "flex", alignItems: "center", gap: "10px",
              p: "8px 12px", border: `1px solid ${C.line}`, borderRadius: "8px",
              fontSize: 13, color: o.text ? C.ink : "var(--color-ink-4)",
            }}>
              <Box sx={{ width: 15, height: 15, borderRadius: "4px", border: `1.5px solid var(--color-hairline-cool)`, flexShrink: 0 }} />
              {o.text || `Option ${i + 1}`}
            </Box>
          ))}
        </Box>
      )}

      {isMatch && (
        <Box sx={{ mt: "12px" }}>
          <Typography sx={{ fontSize: 11, color: C.ink3, fontStyle: "italic", mb: "8px" }}>
            Drag answers to match each prompt
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
            {(q.pairs || []).map((p, i) => (
              <React.Fragment key={p.id}>
                <Box sx={{ p: "7px 10px", bgcolor: "var(--color-surface-2)", borderRadius: "7px", fontSize: 13, color: p.prompt ? C.ink : "var(--color-ink-4)" }}>
                  {p.prompt || `Prompt ${i + 1}`}
                </Box>
                <Box sx={{
                  p: "7px 10px", border: "1.5px dashed var(--color-hairline-cool)", borderRadius: "7px",
                  fontSize: 12, color: "var(--color-ink-4)", display: "flex", alignItems: "center",
                }}>
                  Drop answer here
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}

      {q.type === "SHORT_ANSWER" && (
        <Box sx={{
          mt: "12px", p: "9px 12px", border: `1px solid ${C.line}`, borderRadius: "8px",
          fontSize: 13, color: "var(--color-ink-4)", minHeight: 56,
        }}>
          Type your answer here…
        </Box>
      )}
    </Box>
  );
}

// ─── AddQuestionButton ────────────────────────────────────────────────────────
function AddQuestionButton({ onClick, icon, label, sublabel, accentBg, accentFg }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;

  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        height: 64, border: `1px solid ${C.line}`, background: "#fff",
        borderRadius: "10px", p: "0 14px", display: "flex", alignItems: "center",
        gap: "10px", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
        "&:hover": { background: C.surface2, borderColor: "var(--color-hairline-cool)" },
        "&:focus-visible": { outline: `2px solid ${tPrimary}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{
        width: 28, height: 28, borderRadius: "7px", background: accentBg,
        color: accentFg, display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, "& svg": { fontSize: 16 },
      }}>
        {icon}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1px" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{label}</Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 500, color: C.ink3 }}>{sublabel}</Typography>
      </Box>
    </Box>
  );
}

// ─── CreateTestModal ──────────────────────────────────────────────────────────
/**
 * Auto-saves while the test is edited (see useAutoSave). Each time the editor opens it is mounted fresh, so what it
 * opened with is the saved baseline and only real edits are written.
 */
export default function CreateTestModal(props) {
  return <CreateTestDialog key={props.open ? "open" : "closed"} {...props} />;
}

function CreateTestDialog({ open, onClose, onSave, initial, bank = false }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;
  const tSecondary = theme.palette.secondary.main;
  const tAccent = theme.palette.warning.main;
  const [form, setForm] = useState(() => hydrateInitial(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  // Mounted fresh each time the editor opens (see CreateTestModal); a parent refreshing `initial` must not reset the form.

  const totalPoints = useMemo(
    () => form.questions.reduce((s, q) => s + (Number(q.points) || 0), 0),
    [form.questions],
  );

  const passPct = form.passingScore === ""
    ? 70
    : Math.max(0, Math.min(100, parseInt(form.passingScore, 10) || 0));
  const passLabel = `${passPct}%`;
  const timeLabel = form.timeLimitMinutes === ""
    ? "∞"
    : `${parseInt(form.timeLimitMinutes, 10) || 0} min`;

  const qErrors = useMemo(() => validateQuestions(form.questions), [form.questions]);
  const invalidCount = Object.keys(qErrors).length;
  const isEditing = Boolean(initial);
  const isLive = isEditing && initial.enabled === true;

  const canPublish = form.title.trim().length > 0 && form.questions.length > 0;

  // Live tests are not auto-saved: students may be taking them, so changes go in with Save changes.
  const savedIdRef = useRef(initial?.id ?? null);
  const contentPayload = () => ({
    title: form.title,
    description: form.description,
    passingScore: form.passingScore !== "" ? Number(form.passingScore) : null,
    timeLimitMinutes: form.timeLimitMinutes !== "" ? Number(form.timeLimitMinutes) : null,
    maxRetakes: form.maxRetakes !== "" ? Number(form.maxRetakes) : 1,
    opensAt: !bank && form.opensAt ? form.opensAt + ":00" : null,
    closesAt: !bank && form.closesAt ? form.closesAt + ":00" : null,
    questions: form.questions.map(cleanQuestion),
    tags: JSON.stringify(form.category ? [form.category] : []),
    enabled: isLive,
    ...(isEditing ? { changeNote: form.changeNote.trim() } : {}),
  });

  const autosave = useAutoSave({
    enabled: open && form.title.trim().length > 0 && !isLive,
    data: contentPayload(),
    save: async (content) => {
      const result = await onSave({ ...content, autosave: true, savedBankId: savedIdRef.current });
      if (result?.id) savedIdRef.current = result.id;
    },
  });

  // Closing never waits: a pending change still saves in the background.
  const closeEditor = () => {
    autosave.flush();
    onClose();
  };

  const addQuestion = (type) =>
    setForm((f) => ({ ...f, questions: [...f.questions, makeQuestion(type)] }));

  const updateQuestion = (id, q) =>
    setForm((f) => ({ ...f, questions: f.questions.map((x) => (x.id === id ? q : x)) }));

  const deleteQuestion = (id) =>
    setForm((f) => ({ ...f, questions: f.questions.filter((x) => x.id !== id) }));

  // publish: true = go live, false = draft; null ("Save" on an existing test) keeps its current status.
  const handleSave = async (publishRequested) => {
    if (!form.title.trim()) { setError("Title is required."); return; }
    const publish = publishRequested === null ? isLive : publishRequested;
    if (publish && invalidCount > 0) {
      setShowErrors(true);
      setError(`Fix ${invalidCount} question${invalidCount === 1 ? "" : "s"} before publishing.`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      // An auto-save still running must finish first, so this save updates that copy instead of creating another.
      await autosave.settle();
      await onSave({
        savedBankId: savedIdRef.current,
        title: form.title,
        description: form.description,
        passingScore: form.passingScore !== "" ? Number(form.passingScore) : null,
        timeLimitMinutes: form.timeLimitMinutes !== "" ? Number(form.timeLimitMinutes) : null,
        maxRetakes: form.maxRetakes !== "" ? Number(form.maxRetakes) : 1,
        opensAt: !bank && form.opensAt ? form.opensAt + ":00" : null,
        closesAt: !bank && form.closesAt ? form.closesAt + ":00" : null,
        questions: form.questions.map(cleanQuestion),
        tags: JSON.stringify(form.category ? [form.category] : []),
        enabled: publish,
        ...(isEditing ? { changeNote: form.changeNote.trim() } : {}),
      });
      onClose();
    } catch (e) {
      const d = e?.response?.data;
      setError(d?.error || d?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={closeEditor}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1020px, calc(100vw - 48px))",
          maxHeight: "min(900px, calc(100vh - 48px))",
          borderRadius: "18px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 60px -20px rgba(20,24,40,.28), 0 12px 24px -16px rgba(20,24,40,.18)",
          border: "1px solid rgba(0,0,0,.04)",
          m: 0,
        },
      }}
      slotProps={{ backdrop: { sx: { background: "rgba(20,24,40,.45)" } } }}
    >
      {/* ── Header ── */}
      <Box sx={{
        p: "18px 24px", borderBottom: `1px solid ${C.line2}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "10px",
            background: alpha(tPrimary, 0.08), color: tPrimary,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink, lineHeight: 1.2, fontFamily: "var(--font-display)" }}>
              {isEditing ? "Edit test" : "Create test"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.ink3, mt: "2px" }}>
              {form.title.trim() || "Untitled"} · {isLive ? "live" : "draft"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={{
            height: 22, px: "8px", borderRadius: "999px",
            background: isLive ? "#E7F6EC" : C.draftBg, color: isLive ? "#1A7F37" : C.draftFg,
            fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center",
          }}>
            {isLive ? "● Published" : "◦ Not published"}
          </Box>
          <Tooltip title="Close">
            <IconButton onClick={closeEditor} size="small" aria-label="Close"
              sx={{ width: 36, height: 36, borderRadius: "10px", color: C.ink2, "&:hover": { background: C.surface3 } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Summary strip ── */}
      <Box sx={{ p: "14px 24px", borderBottom: `1px solid ${C.line2}`, background: "#fff", flexShrink: 0 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
          <SummaryStat label="Questions"    value={String(form.questions.length)} icon={<FormatListBulletedIcon />} color={C.blue} />
          <SummaryStat label="Pass score"   value={passLabel}                      icon={<GpsFixedIcon />}          color={tSecondary} />
          <SummaryStat label="Time limit"   value={timeLabel}                      icon={<AccessTimeIcon />}        color={tSecondary} />
          <SummaryStat label="Total points" value={String(totalPoints)}            icon={<AutoAwesomeIcon />}       color={tAccent} />
        </Box>
      </Box>

      {/* ── Body (split) ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1.05fr 1fr", flex: 1, minHeight: 0, overflow: "hidden" }}>

        {/* Form column */}
        <Box sx={{ p: "22px 24px", borderRight: `1px solid ${C.line2}`, overflowY: "auto" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: C.ink3, mb: "14px" }}>
            Details
          </Typography>

          {/* Title */}
          <Box sx={{ mb: "16px" }}>
            <Typography component="label" htmlFor="ct-title" sx={{ fontSize: 12, fontWeight: 600, color: C.ink2, letterSpacing: "0.02em", display: "block", mb: "6px" }}>
              Title <Box component="span" sx={{ color: C.danger }}>*</Box>
            </Typography>
            <TextField
              id="ct-title"
              fullWidth
              size="small"
              placeholder="e.g. Fire safety – quarterly check"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              error={Boolean(error && !form.title.trim())}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px", background: "#fff", fontSize: 14,
                  "& fieldset": { borderColor: C.line },
                  "&:hover fieldset": { borderColor: C.ink3 },
                  "&.Mui-focused": {
                    background: "#fff",
                    "& fieldset": { borderColor: tPrimary, borderWidth: "2px" },
                    boxShadow: `0 0 0 4px ${alpha(tPrimary, 0.10)}`,
                  },
                },
              }}
            />
          </Box>

          {/* Description */}
          <Box sx={{ mb: "16px" }}>
            <Typography component="label" htmlFor="ct-desc" sx={{ fontSize: 12, fontWeight: 600, color: C.ink2, letterSpacing: "0.02em", display: "block", mb: "6px" }}>
              Description
            </Typography>
            <TextField
              id="ct-desc"
              fullWidth
              multiline
              minRows={3}
              size="small"
              placeholder="Optional context for students…"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px", background: "#fff", fontSize: 14, lineHeight: 1.5,
                  alignItems: "flex-start",
                  "& fieldset": { borderColor: C.line },
                  "&:hover fieldset": { borderColor: C.ink3 },
                  "&.Mui-focused": {
                    background: "#fff",
                    "& fieldset": { borderColor: tPrimary, borderWidth: "2px" },
                    boxShadow: `0 0 0 4px ${alpha(tPrimary, 0.10)}`,
                  },
                },
              }}
            />
          </Box>

          {/* What changed? (edit only) */}
          {isEditing && (
            <Box sx={{ mb: "16px" }}>
              <Typography component="label" htmlFor="ct-changenote" sx={{ fontSize: 12, fontWeight: 600, color: C.ink2, letterSpacing: "0.02em", display: "block", mb: "6px" }}>
                What changed? (optional)
              </Typography>
              <TextField
                id="ct-changenote"
                fullWidth
                size="small"
                placeholder="e.g. Fixed typo in question 3, added new scenario"
                value={form.changeNote}
                onChange={(e) => setForm({ ...form, changeNote: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px", background: "#fff", fontSize: 14,
                    "& fieldset": { borderColor: C.line },
                    "&:hover fieldset": { borderColor: C.ink3 },
                    "&.Mui-focused": {
                      background: "#fff",
                      "& fieldset": { borderColor: tPrimary, borderWidth: "2px" },
                      boxShadow: `0 0 0 4px ${alpha(tPrimary, 0.10)}`,
                    },
                  },
                }}
              />
            </Box>
          )}

          {/* Category */}
          <Box sx={{ mb: "16px" }}>
            <Typography component="label" sx={{ fontSize: 12, fontWeight: 600, color: C.ink2, letterSpacing: "0.02em", display: "block", mb: "6px" }}>
              Category
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={form.category}
                displayEmpty
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                sx={{ borderRadius: "10px", background: "#fff", fontSize: 14, "& fieldset": { borderColor: C.line }, "&:hover fieldset": { borderColor: C.ink3 }, "&.Mui-focused fieldset": { borderColor: tPrimary, borderWidth: "2px" } }}
              >
                <MenuItem value=""><em style={{ color: C.ink3, fontStyle: "normal" }}>Select a category…</em></MenuItem>
                {["Firefighter","EMS","Hazmat","Search & Rescue","Fire Officer","General"].map(c => (
                  <MenuItem key={c} value={c} sx={{ fontSize: 14 }}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Pass + Time + Retakes */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px", mb: "8px" }}>
            <Box>
              <Typography component="label" htmlFor="ct-pass" sx={{ fontSize: 12, fontWeight: 600, color: C.ink2, letterSpacing: "0.02em", display: "block", mb: "6px" }}>
                Passing score
              </Typography>
              <TextField
                id="ct-pass"
                fullWidth
                size="small"
                placeholder="70"
                value={form.passingScore}
                onChange={(e) => setForm({ ...form, passingScore: e.target.value.replace(/[^0-9]/g, "").slice(0, 3) })}
                inputProps={{ inputMode: "numeric" }}
                InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 12, color: C.ink3 }}>%</Typography></InputAdornment> }}
                helperText="70% default if blank"
                FormHelperTextProps={{ sx: { fontSize: 12, color: C.ink3, mt: "4px", ml: 0 } }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px", background: "#fff", fontSize: 14,
                    "& fieldset": { borderColor: C.line },
                    "&:hover fieldset": { borderColor: C.ink3 },
                    "&.Mui-focused": {
                      background: "#fff",
                      "& fieldset": { borderColor: tPrimary, borderWidth: "2px" },
                      boxShadow: `0 0 0 4px ${alpha(tPrimary, 0.10)}`,
                    },
                  },
                }}
              />
            </Box>
            <Box>
              <Typography component="label" htmlFor="ct-time" sx={{ fontSize: 12, fontWeight: 600, color: C.ink2, letterSpacing: "0.02em", display: "block", mb: "6px" }}>
                Time limit
              </Typography>
              <TextField
                id="ct-time"
                fullWidth
                size="small"
                placeholder="∞"
                value={form.timeLimitMinutes}
                onChange={(e) => setForm({ ...form, timeLimitMinutes: e.target.value.replace(/[^0-9]/g, "").slice(0, 3) })}
                inputProps={{ inputMode: "numeric" }}
                InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 12, color: C.ink3 }}>min</Typography></InputAdornment> }}
                helperText="Untimed if blank"
                FormHelperTextProps={{ sx: { fontSize: 12, color: C.ink3, mt: "4px", ml: 0 } }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px", background: "#fff", fontSize: 14,
                    "& fieldset": { borderColor: C.line },
                    "&:hover fieldset": { borderColor: C.ink3 },
                    "&.Mui-focused": {
                      background: "#fff",
                      "& fieldset": { borderColor: tPrimary, borderWidth: "2px" },
                      boxShadow: `0 0 0 4px ${alpha(tPrimary, 0.10)}`,
                    },
                  },
                }}
              />
            </Box>
            <Box>
              <Typography component="label" htmlFor="ct-retakes" sx={{ fontSize: 12, fontWeight: 600, color: C.ink2, letterSpacing: "0.02em", display: "block", mb: "6px" }}>
                Attempts allowed
              </Typography>
              <TextField
                id="ct-retakes"
                fullWidth
                size="small"
                placeholder="1"
                value={form.maxRetakes}
                onChange={(e) => setForm({ ...form, maxRetakes: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) })}
                inputProps={{ inputMode: "numeric" }}
                helperText="Total attempts per student (1 = no retakes)"
                FormHelperTextProps={{ sx: { fontSize: 12, color: C.ink3, mt: "4px", ml: 0 } }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px", background: "#fff", fontSize: 14,
                    "& fieldset": { borderColor: C.line },
                    "&:hover fieldset": { borderColor: C.ink3 },
                    "&.Mui-focused": {
                      background: "#fff",
                      "& fieldset": { borderColor: tPrimary, borderWidth: "2px" },
                      boxShadow: `0 0 0 4px ${alpha(tPrimary, 0.10)}`,
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Opens / Closes */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", mt: "8px", mb: "8px" }}>
            {[
              { id: "ct-opens", label: "Opens", key: "opensAt", hint: "Optional — students see test after this time" },
              { id: "ct-closes", label: "Closes", key: "closesAt", hint: "Optional — no submissions after this time" },
            ].filter(() => !bank).map(({ id, label, key, hint }) => (
              <Box key={key}>
                <Typography component="label" htmlFor={id} sx={{ fontSize: 12, fontWeight: 600, color: C.ink2, letterSpacing: "0.02em", display: "block", mb: "6px" }}>
                  {label}
                </Typography>
                <TextField
                  id={id}
                  fullWidth
                  size="small"
                  type="datetime-local"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText={hint}
                  FormHelperTextProps={{ sx: { fontSize: 12, color: C.ink3, mt: "4px", ml: 0 } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px", background: "#fff", fontSize: 14,
                      "& fieldset": { borderColor: C.line },
                      "&:hover fieldset": { borderColor: C.ink3 },
                      "&.Mui-focused": {
                        background: "#fff",
                        "& fieldset": { borderColor: tPrimary, borderWidth: "2px" },
                        boxShadow: `0 0 0 4px ${alpha(tPrimary, 0.10)}`,
                      },
                    },
                    "& input[type=datetime-local]::-webkit-calendar-picker-indicator": {
                      opacity: 0.5, cursor: "pointer",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: "20px", borderColor: C.line2 }} />

          {/* Questions header */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: C.ink3, mb: "12px" }}>
            Questions{" "}
            <Box component="span" sx={{ color: C.ink2 }}>· {form.questions.length}</Box>
          </Typography>

          {error && (
            <Typography sx={{ fontSize: 13, color: C.danger, mb: "12px" }}>{error}</Typography>
          )}

          {/* Question editors */}
          {form.questions.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: "14px" }}>
              {form.questions.map((q, i) => (
                <QuestionEditor
                  key={q.id}
                  q={q}
                  n={i + 1}
                  onChange={(next) => updateQuestion(q.id, next)}
                  onDelete={() => deleteQuestion(q.id)}
                  error={showErrors ? qErrors[q.id] : undefined}
                />
              ))}
            </Box>
          )}

          {/* Add buttons */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <AddQuestionButton
              onClick={() => addQuestion("MULTIPLE_CHOICE")}
              icon={<FormatListBulletedIcon />}
              label="Multiple choice"
              sublabel="One correct answer"
              accentBg="rgba(37,99,235,.10)"
              accentFg={C.blue}
            />
            <AddQuestionButton
              onClick={() => addQuestion("TRUE_FALSE")}
              icon={<ToggleOffIcon />}
              label="True / false"
              sublabel="Quick binary check"
              accentBg={alpha(tSecondary, 0.10)}
              accentFg={tSecondary}
            />
            <AddQuestionButton
              onClick={() => addQuestion("MULTIPLE_SELECT")}
              icon={<CheckBoxOutlinedIcon />}
              label="Select all that apply"
              sublabel="Multiple correct answers"
              accentBg={alpha(tAccent, 0.10)}
              accentFg={tAccent}
            />
            <AddQuestionButton
              onClick={() => addQuestion("MATCHING")}
              icon={<SwapHorizIcon />}
              label="Matching"
              sublabel="Drag to match pairs"
              accentBg="rgba(124,58,237,.10)"
              accentFg="var(--color-accent-violet)"
            />
            <AddQuestionButton
              onClick={() => addQuestion("SHORT_ANSWER")}
              icon={<ShortTextIcon />}
              label="Short answer"
              sublabel="Typed answer, auto-graded"
              accentBg="rgba(13,148,136,.10)"
              accentFg="#0D9488"
            />
          </Box>
        </Box>

        {/* Preview column */}
        <Box sx={{ background: C.surface3, p: "22px 24px", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px", flexShrink: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: C.ink3 }}>
              Student view · preview
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 11, color: C.ink3 }}>
              <VisibilityIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: 11, color: C.ink3 }}>Live</Typography>
            </Box>
          </Box>

          <Box sx={{
            background: "#fff", borderRadius: "14px", p: "20px 22px",
            border: `1px solid ${C.line}`, flex: 1, overflow: "auto",
            boxShadow: "0 1px 0 rgba(0,0,0,.03)",
          }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: tPrimary }}>
              Test
            </Typography>
            <Typography sx={{
              fontSize: 20, fontWeight: 700, mt: "4px", letterSpacing: "-0.01em",
              color: form.title.trim() ? C.ink : "var(--color-ink-4)",
              fontFamily: "var(--font-display)",
            }}>
              {form.title.trim() || "Untitled test"}
            </Typography>
            {form.description.trim() && (
              <Typography sx={{ fontSize: 13, color: C.ink2, mt: "6px", lineHeight: 1.45 }}>
                {form.description}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: "14px", mt: "10px", flexWrap: "wrap" }}>
              {[
                { icon: <GpsFixedIcon sx={{ fontSize: 14 }} />, label: `Pass ${passLabel}` },
                { icon: <AccessTimeIcon sx={{ fontSize: 14 }} />, label: form.timeLimitMinutes === "" ? "Untimed" : `${parseInt(form.timeLimitMinutes, 10) || 0} min` },
                { icon: <FormatListBulletedIcon sx={{ fontSize: 14 }} />, label: `${form.questions.length} question${form.questions.length === 1 ? "" : "s"}` },
                { icon: <AutoAwesomeIcon sx={{ fontSize: 14 }} />, label: `${totalPoints} pts` },
              ].map(({ icon, label }) => (
                <Box key={label} sx={{ display: "flex", gap: "6px", alignItems: "center", fontSize: 12, color: C.ink2 }}>
                  {icon}
                  <Typography sx={{ fontSize: 12, color: C.ink2 }}>{label}</Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: "16px", borderColor: C.line2 }} />

            {form.questions.length === 0 ? (
              <>
                <Typography sx={{ fontSize: 12, color: C.ink3, mb: "10px" }}>Sample · how a question will look</Typography>
                <Box sx={{ border: `1px dashed ${C.line}`, borderRadius: "10px", p: "14px", background: C.surface2 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.ink, mb: "6px" }}>Question 1</Typography>
                  <Typography sx={{ fontSize: 14, color: C.ink }}>Which extinguisher class is used for electrical fires?</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: "12px" }}>
                    {["Class A – solids", "Class B – liquids", "Class C – electrical", "Class K – cooking oils"].map((t) => (
                      <Box key={t} sx={{
                        display: "flex", alignItems: "center", gap: "10px",
                        p: "9px 12px", background: "#fff", border: `1px solid ${C.line}`,
                        borderRadius: "8px", fontSize: 13, color: C.ink,
                      }}>
                        <Box sx={{ width: 15, height: 15, borderRadius: "50%", border: `1.5px solid var(--color-hairline-cool)`, flexShrink: 0 }} />
                        {t}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            ) : (
              form.questions.map((q, i) => (
                <PreviewQuestion key={q.id} q={q} n={i + 1} />
              ))
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        p: "14px 24px", borderTop: `1px solid ${C.line2}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#fff", flexShrink: 0,
      }}>
        <Typography sx={{ fontSize: 12, color: C.ink3 }}>
          {form.questions.length === 0
            ? "Add at least one question to publish"
            : `Ready · ${form.questions.length} question${form.questions.length === 1 ? "" : "s"}, ${totalPoints} pts`}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <AutoSaveStatus status={autosave.status} savedAt={autosave.savedAt} error={autosave.error} published={isLive} />
          <Button onClick={closeEditor} disabled={saving}
            sx={{
              height: 42, px: "18px", borderRadius: "10px", fontSize: 14, fontWeight: 600,
              color: C.ink2, background: "transparent",
              "&:hover": { background: C.surface3, textDecoration: "none" },
            }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSave(isEditing ? null : false)}
            disabled={saving || !form.title.trim()}
            sx={{
              height: 42, px: "18px", borderRadius: "10px", fontSize: 14, fontWeight: 600,
              color: C.ink, background: "#fff", border: `1px solid ${C.line}`,
              "&:hover": { background: C.surface2, borderColor: "var(--color-hairline-cool)" },
              "&.Mui-disabled": { opacity: 0.5 },
            }}>
            {isLive ? "Save changes" : isEditing ? "Save" : "Save as draft"}
          </Button>
          <Tooltip title={!canPublish ? "Add a title and at least one question" : ""}>
            <span>
              <Button
                variant="contained"
                onClick={() => handleSave(true)}
                disabled={saving || !canPublish}
                sx={{
                  height: 42, px: "18px", borderRadius: "10px", fontSize: 14, fontWeight: 600,
                  background: `linear-gradient(180deg, ${tPrimaryDk} 0%, ${tPrimary} 100%)`,
                  "&:hover": { background: `linear-gradient(180deg, ${tPrimary} 0%, ${alpha(tPrimary, 0.75)} 100%)` },
                  "&.Mui-disabled": { opacity: 0.5, background: tPrimary, color: "#fff" },
                  boxShadow: "none",
                }}>
                {saving ? "Saving…" : "Publish"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Dialog>
  );
}
